"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "@/context/AuthContext";
import { getDefaultTheme, isKnownTheme, resolveTheme } from "./registry";
import type { ThemeDefinition, ThemeId } from "./types";

interface ThemeContextValue {
  theme: ThemeDefinition;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: getDefaultTheme(),
});

/**
 * Reads a theme id from a `customizations` field on a client or group.
 *
 * The DB value is JSONB, which Sequelize hands back as either a parsed object
 * or (in some legacy rows) a JSON string. Both shapes are handled here.
 *
 * Returns `null` when the value is missing, malformed, or names a theme that
 * is not in the registry — the resolver then falls through to the next
 * priority level.
 */
const readThemeIdFromCustomizations = (
  customizations: unknown
): ThemeId | null => {
  if (!customizations) return null;

  const parsed =
    typeof customizations === "string"
      ? safeJsonParse(customizations)
      : customizations;

  if (!parsed || typeof parsed !== "object") return null;

  const themeId = (parsed as { theme?: unknown }).theme;
  if (typeof themeId !== "string") return null;

  return isKnownTheme(themeId) ? themeId : null;
};

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

type AuthUser = ReturnType<typeof useAuth>["user"];

/**
 * Selects the active theme id for the current user.
 *
 * Priority (preserved from the previous ThemeContext implementation):
 *   1. Group customizations — but only when the user belongs to exactly one
 *      group. Multi-group users fall through to the client theme because
 *      there is no defined way to pick "the" group.
 *   2. Client customizations.
 *   3. Default theme.
 */
const selectThemeId = (user: AuthUser): ThemeId => {
  if (!user) return "default";

  const groupThemeId =
    user.sf_groups?.length === 1
      ? readThemeIdFromCustomizations(user.sf_groups[0]?.customizations)
      : null;
  if (groupThemeId) return groupThemeId;

  const clientThemeId = readThemeIdFromCustomizations(
    user.sf_clients?.[0]?.customizations
  );
  if (clientThemeId) return clientThemeId;

  return "default";
};

/**
 * Provider that resolves the active theme from the authenticated user and
 * keeps the legacy `data-theme` HTML attribute in sync so the existing CSS
 * overrides under `html[data-theme="b2b"] { ... }` etc. in globals.css keep
 * working until they are migrated into per-theme tokens.css / components/.
 */
export const ThemeBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [themeId, setThemeId] = useState<ThemeId>("default");

  useEffect(() => {
    try {
      const resolved = selectThemeId(user);
      setThemeId(resolved);

      if (resolved === "default") {
        document.documentElement.removeAttribute("data-theme");
      } else {
        document.documentElement.setAttribute("data-theme", resolved);
      }
    } catch {
      document.documentElement.removeAttribute("data-theme");
      setThemeId("default");
    }
  }, [user]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme: resolveTheme(themeId) }),
    [themeId]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

/** Returns the active resolved theme. */
export const useTheme = (): ThemeDefinition => useContext(ThemeContext).theme;
